import { getLocale, useTranslation } from '@rhino-project/core/i18n';
import { Typography } from '@heroui/react';

export type CalendarPreviewEvent = {
  uid: string;
  title: string;
  location: string | null;
  start_at: string;
};

export type CalendarIcsPreviewProps = {
  isLoading: boolean;
  count?: number;
  events?: CalendarPreviewEvent[];
  fetchError?: string;
};

export function CalendarIcsPreview({
  isLoading,
  count,
  events,
  fetchError
}: CalendarIcsPreviewProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Typography.Paragraph color="muted">
        {t('frontend.messages.checking_feed')}
      </Typography.Paragraph>
    );
  }

  if (fetchError) {
    return <p className="text-sm text-danger">{fetchError}</p>;
  }

  if (!events) return null;

  const total = count ?? events.length;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-success">
        ✓ {t('frontend.messages.upcoming_events', { count: total })}
      </p>
      {events.length > 0 && (
        <ul className="flex flex-col divide-y divide-separator">
          {events.slice(0, 5).map((event) => (
            <li
              key={`${event.uid}-${event.start_at}`}
              className="py-2 flex items-baseline justify-between gap-3"
            >
              <span className="text-sm">{event.title}</span>
              <span className="text-sm text-muted whitespace-nowrap">
                {new Date(event.start_at).toLocaleString(
                  getLocale().replaceAll('_', '-'),
                  {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  }
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
